import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import axios, { AxiosError } from 'axios'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'

import { getAppIsLdInitialized } from './appSelectors'
import { setZendeskAuthenticatedUserId } from './appSlice'
import { getAuthSessionUserId } from './auth/authSelectors'
import { RootState } from './store'
import { axiosAuthInstance } from '../common/http/axiosInstance'
import { logToSentry } from '../common/utils/utils'
import { clearZendeskIdentity } from '../common/utils/zendeskMessaging'
import { getLTVideoSessionOpen } from '../lyraTherapy/data/ltVideoSelectors'

type Messenger = (
  channel: 'messenger',
  command: 'hide' | 'show' | 'logoutUser' | 'loginUser',
  jwtCallback?: (callback: (jwt: string) => void) => void,
  loginCallback?: (error: unknown) => void,
) => void

const loadZendesk = (): Messenger => {
  // Keep one SDK instance across portal logout/login. Its identity is cleared separately.
  ;(window as any).zE ||
    (function (e, t, s) {
      const n: any =
        ((window as any).zE =
        (window as any).zEmbed =
          function () {
            n._.push(arguments)
          })
      const script = (n.s = e.createElement(t)) as HTMLScriptElement
      n._ = []
      n.set = function () {
        n.set._.push(arguments)
      }
      n.set._ = []
      n.t = Date.now()
      script.async = true
      script.charset = 'utf-8'
      script.src = 'https://static.zdassets.com/ekr/snippet.js?key=' + s
      script.onerror = () => {
        logToSentry('zendeskWidgetError', { message: 'Failed to load Zendesk Messaging', type: 'error' })
      }
      e.head.appendChild(script)
    })(document, 'script', '4d34cb90-aac8-486d-9dfd-a1886fe938fd')

  // Resolve the current SDK function for every command; the loader queue may be replaced.
  return (...args) => (window as any).zE(...args)
}

const waitForRetry = (delay: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const finish = () => {
      clearTimeout(timer)
      signal.removeEventListener('abort', finish)
      // Timeout and abort share this path; completing removes both triggers.
      // oxlint-disable-next-line promise/no-multiple-resolved
      resolve()
    }
    const timer = setTimeout(finish, delay)
    signal.addEventListener('abort', finish, { once: true })
    if (signal.aborted) finish()
  })

const ZendeskWidget = () => {
  const userId = useSelector(getAuthSessionUserId)
  const isLdInitialized = useSelector(getAppIsLdInitialized)
  const dispatch = useDispatch()
  const { isZendeskMessagingAuthenticationEnabled = false } = useFlags()
  const messagingEnabled = isLdInitialized && isZendeskMessagingAuthenticationEnabled
  const authenticatedUserId = useSelector((state: RootState) => state.app.zendeskAuthenticatedUserId)
  const videoSessionOpen = useSelector(getLTVideoSessionOpen)

  useEffect(() => {
    if (!messagingEnabled || !userId) {
      // Disabling Messaging never falls back to an anonymous or Classic widget.
      clearZendeskIdentity()
      return
    }

    let active = true
    const controller = new AbortController()
    let messenger: Messenger | undefined

    const failAuthentication = () => {
      if (!active) return
      active = false
      controller.abort()
      dispatch(setZendeskAuthenticatedUserId(undefined))
      clearZendeskIdentity()
      logToSentry('zendeskWidgetError', { message: 'Zendesk Messaging authentication failed', type: 'error' })
    }

    try {
      messenger = loadZendesk()
      dispatch(setZendeskAuthenticatedUserId(undefined))
      clearZendeskIdentity()
      // Zendesk calls the token supplier when needed, then reports login success separately.
      messenger(
        'messenger',
        'loginUser',
        async (callback) => {
          // Keep supplying this callback after outages; the SDK need not request it again.
          let retryDelay = 1000
          while (!controller.signal.aborted) {
            try {
              const { data } = await axiosAuthInstance.get<{ id: string; zendesk_jwt: string }>('/v1/user', {
                signal: controller.signal,
                timeout: 10000,
                // Bypass the interceptor's shared cancelLastRequest slot, which pages cancel on unmount.
                cancelToken: axios.CancelToken.source().token,
                headers: { 'Cache-Control': 'no-cache' },
              })
              if (!active) return
              if (data.id !== userId || !data.zendesk_jwt) {
                failAuthentication()
                return
              }
              callback(data.zendesk_jwt)
              return
            } catch (error) {
              if (!active || axios.isCancel(error)) return
              const status = (error as AxiosError).response?.status
              if (status && status < 500 && status !== 408 && status !== 429) {
                failAuthentication()
                return
              }
              if (retryDelay === 4000)
                logToSentry('zendeskWidgetError', {
                  message: 'Zendesk Messaging token temporarily unavailable',
                  type: 'warning',
                })
              await waitForRetry(retryDelay, controller.signal)
              // Cap the retry rate during a longer outage; teardown cancels the wait.
              retryDelay = Math.min(retryDelay * 2, 30000)
            }
          }
        },
        (error) => {
          if (!active) return
          if (error) {
            failAuthentication()
            return
          }
          // Having a token is not enough: only expose chat once Zendesk accepts it.
          dispatch(setZendeskAuthenticatedUserId(userId))
        },
      )
    } catch {
      active = false
      controller.abort()
      dispatch(setZendeskAuthenticatedUserId(undefined))
      logToSentry('zendeskWidgetError', { message: 'Failed to initialize Zendesk Messaging', type: 'error' })
    }

    return () => {
      // Cancellation can arrive too late, so also prevent old callbacks from using their results.
      active = false
      controller.abort()
      dispatch(setZendeskAuthenticatedUserId(undefined))
      clearZendeskIdentity()
    }
  }, [dispatch, messagingEnabled, userId])

  useEffect(() => {
    if (!messagingEnabled || !(window as any).zE) return
    // Video-session teardown must never reveal an unauthenticated launcher.
    try {
      ;(window as any).zE(
        'messenger',
        authenticatedUserId === userId && !!userId && !videoSessionOpen ? 'show' : 'hide',
      )
    } catch {
      logToSentry('zendeskWidgetError', { message: 'Failed to update Zendesk Messaging visibility', type: 'error' })
    }
  }, [messagingEnabled, authenticatedUserId, userId, videoSessionOpen])

  return null
}

export default ZendeskWidget
